import { Grid, SvgIcon } from '@material-ui/core';
import { usePagination } from '@ncr-retail/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ITransactionSearchState,
  TransactionSearchContext,
} from 'src/common/interfaces/states/ITransactionSearchState';
import Pagination from 'src/pagination/pagination';
import BaseButton from '../common/components/base-button';
import { IButton } from '../common/interfaces/IButton';
import { POSModeState } from '../common/interfaces/states/POSModeState';
import { AppState } from '../common/redux/configure-store';
import { trainingModeStyles } from '../common/training-mode/training-mode-styles';
import Keypad from '../keypad/components/keypad';
import { SearchResultsCategoryHead } from './components/search-results-category-head';
import { SearchResultsScreenHead } from './components/search-results-screen-head';
import { DateRange, ReturnPolicyResponse } from './interfaces/get-effective-return-policy-response';
import {
  SearchTransactionsResponse,
  TransactionResult,
} from './interfaces/transaction-search-response';
import { ReactComponent as GoBackIcon } from './media/go-back-icon.svg';
import { ReactComponent as Print } from '../common/media/print.svg';
import { TransactionSearchResultListings } from './transaction-search-result-list';
import { transactionSearchModalStyles } from './transaction-based-return-styles';
import { handleReprintReceipt } from 'src/reprint-receipt/business-logic/handle-reprint-receipt';

const pageSize = 8;
const fetchMoreItems = 30;

export interface TransactionSearchResultsProps {
  setSearchParam: (searchParam: Partial<ITransactionSearchState>) => void;
  onSearch: (itemCode?: string | undefined) => Promise<void>;
  searchTrasactionResponse: SearchTransactionsResponse;
  backToPreviousPage: () => void;
  onCancel: () => void;
  onSubmit: (tlogId: string) => Promise<void>;
  effectivePolicy?: ReturnPolicyResponse;
  pageNumber: number;
  testIdPrefix: string;
  searchContext?: TransactionSearchContext;
}

const TransactionSearchResultsScreen = (props: TransactionSearchResultsProps): JSX.Element => {
  const {
    searchTrasactionResponse,
    backToPreviousPage,
    setSearchParam,
    onSearch,
    onCancel,
    onSubmit,
    effectivePolicy,
    pageNumber,
    testIdPrefix,
    searchContext,
  } = props;
  const classes = transactionSearchModalStyles();
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const [transactionDetail, setTransactionDetail] = useState<TransactionResult>();
  const leftHanded = useSelector((state: AppState): boolean => state.cashierPreferences.leftHanded);
  const posModeState = useSelector((state: AppState): POSModeState => state.posMode);

  const paginationState = usePagination({
    totalItems: searchTrasactionResponse.totalResults || undefined,
    initialPageSize: pageSize,
  });

  let subHeaderText;
  switch (searchContext) {
    case TransactionSearchContext.TBR:
      subHeaderText = 'TBR_Helper_Text';
      break;
    case TransactionSearchContext.ReprintReceipt:
      subHeaderText = 'ReprintReceipt_Helper_Text';
      break;
  }
  // TODO: useCallback dependency is unknown. Needs to be updated
  // eslint-disable-next-line
  const setPage = useCallback(paginationState.setPage, []);

  useEffect(() => {
    if (pageNumber === 0) {
      setPage(0);
    }
  }, [pageNumber, setPage]);

  const onPageChange = (page: number) => {
    if (page > paginationState.currentPage + 1) {
      const nextPage = pageNumber + 1;
      const shouldCallSearchApi =
        pageSize * (paginationState.currentPage + 3) >= nextPage * fetchMoreItems &&
        nextPage < searchTrasactionResponse.totalPages;
      if (shouldCallSearchApi) {
        setSearchParam({ pageNumber: pageNumber + 1 });
        onSearch();
      }
      paginationState.setNextPage();
    } else {
      paginationState.setPreviousPage();
    }
    setTransactionDetail(undefined);
  };

  const onKeypadSubmit = (itemCode: string): void => {
    if (itemCode) {
      onSearch(itemCode);
    } else {
      if (transactionDetail) {
        onSubmit(transactionDetail.tlogId);
      }
    }
  };

  const listings = searchTrasactionResponse.pageContent?.slice(
    paginationState.currentPage * paginationState.pageSize,
    (paginationState.currentPage + 1) * paginationState.pageSize,
  );

  const keypadSubmitButton: IButton = {
    label: t('Ok'),
    onClick: onKeypadSubmit,
    testId: `${testIdPrefix}-screen-keypad-button`,
  };

  const onSetDateRange = (dateRange: DateRange) => {
    setSearchParam({ pageNumber: 0, dateRange });
    onSearch();
  };

  const onSelect = (selectedTransaction: TransactionResult) => {
    if (
      selectedTransaction.transactionNumber === transactionDetail?.transactionNumber &&
      selectedTransaction.endTransactionDateTimeUtc === transactionDetail?.endTransactionDateTimeUtc
    ) {
      setTransactionDetail(undefined);
    } else {
      setTransactionDetail(selectedTransaction);
    }
  };

  return (
    <Grid
      item
      container
      className={`fullHeight flexFull`}
      direction={leftHanded ? 'row-reverse' : 'row'}
      data-testid='transaction-search-results-screen-container'
    >
      <Grid
        item
        className={`${
          posModeState.trainingMode
            ? trainingModeClasses.trainingModeBackground
            : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.returnItemContainer} ${classes.outerFlexDesgin} ${classes.outerContainerPadding}`}
        >
          <SearchResultsScreenHead
            effectivePolicy={effectivePolicy}
            setDateRange={onSetDateRange}
            listCount={searchTrasactionResponse.totalResults}
            subHeadingText={subHeaderText}
          />
          <Grid container className={classes.autoFlex}>
            <Grid item container className={classes.resultListContainerWidth}>
              <Grid container className={classes.contentAlign}>
                <SearchResultsCategoryHead />
                <Grid
                  container
                  item
                  direction='column'
                  data-testid='transaction-list-results'
                  className={`${classes.tbrListItems} ${classes.customTopMargin}`}
                >
                  <Grid item>
                    {listings?.map(
                      (listing): JSX.Element => (
                        <TransactionSearchResultListings
                          listing={listing}
                          selected={
                            listing.transactionNumber === transactionDetail?.transactionNumber &&
                            listing.endTransactionDateTimeUtc ===
                              transactionDetail?.endTransactionDateTimeUtc
                          }
                          onSelect={onSelect}
                          key={listing.transactionNumber}
                          testIdPrefix={testIdPrefix}
                        />
                      ),
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item container className={`${classes.fitHeight} ${classes.flexEndAlign}`}>
                <Grid item container xs={5}>
                  <Pagination
                    currentPage={paginationState.currentPage + 1}
                    totalPages={paginationState.totalPages}
                    updateCurrentPageHandler={onPageChange}
                    displayText={true}
                    testIdPrefix={`${testIdPrefix}-transaction-list`}
                  />
                </Grid>
                <Grid item container xs={7} justifyContent='flex-end'>
                  <BaseButton
                    buttonType='white'
                    className={classes.actionButtons}
                    onClick={backToPreviousPage}
                    startIcon={
                      <SvgIcon>
                        <GoBackIcon />
                      </SvgIcon>
                    }
                    data-testid={'transaction-search-screen-previous-button'}
                  >
                    {t('Back')}
                  </BaseButton>
                  <BaseButton
                    buttonType='white'
                    className={classes.actionButtons}
                    onClick={onCancel}
                    data-testid={'transaction-search-screen-cancel-button'}
                  >
                    {t('Cancel')}
                  </BaseButton>
                  {searchContext === TransactionSearchContext.ReprintReceipt && (
                    <BaseButton
                      buttonType='white'
                      className={classes.altActionButtons}
                      onClick={() =>
                        handleReprintReceipt(transactionDetail ? transactionDetail.tlogId : '')
                      }
                      disabled={!transactionDetail}
                      startIcon={
                        <SvgIcon>
                          <Print />
                        </SvgIcon>
                      }
                      data-testid={'transaction-search-reprint-receipt-button'}
                    >
                      {'Print Receipt'}
                    </BaseButton>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item className={`${classes.keypadContainer} ${classes.keypadContainerWidth}`}>
              <Keypad
                visible={true}
                keyboardId={testIdPrefix}
                inputFieldText={''}
                keypadTestIdPrefix={`${testIdPrefix}-`}
                topLabelText={t('ScanItemBarcodeText')}
                submitButton={keypadSubmitButton}
                disableDecimalInput={true}
                disableManualWeight={true}
                disableQuantity={true}
                inputInstructionsLabelText={t('Keypad_Input_Helper_text')}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TransactionSearchResultsScreen;
